const BASE = process.env.REACT_APP_API_BASE_URL;

const getAuthHeaders = (json = true) => {
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

const handleUnauth = (res) => {
  if (res.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  return res;
};

const jsonOrThrow = async (res, errMsg) => {
  handleUnauth(res);
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.message || ''; } catch {}
    throw new Error(detail || errMsg);
  }
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

// ─── ADMIN ─────────────────────────────────────────────────────
export const adminAddInterviewer = (data) =>
  fetch(`${BASE}/api/admin/interviewers`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to add interviewer'));

export const adminGetInterviewers = () =>
  fetch(`${BASE}/api/admin/interviewers`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load interviewers'));

export const adminToggleInterviewerStatus = (id) =>
  fetch(`${BASE}/api/admin/interviewers/${id}/toggle-status`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  }).then((r) => jsonOrThrow(r, 'Failed to toggle status'));

export const adminAddSlot = (data) =>
  fetch(`${BASE}/api/admin/interviewers/slots`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to add slot'));

// ─── USER INTERVIEW ───────────────────────────────────────────
export const getAvailableSlots = (interviewerId) =>
  fetch(`${BASE}/api/interviews/slots/${interviewerId}`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load slots'));

export const bookInterview = (data) =>
  fetch(`${BASE}/api/interviews/book`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to book interview'));

export const confirmReschedule = (data) =>
  fetch(`${BASE}/api/interviews/confirm-reschedule`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to confirm reschedule'));

export const getMyRequests = () =>
  fetch(`${BASE}/api/interviews/my-requests`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load requests'));

export const getMySubscription = () =>
  fetch(`${BASE}/api/interviews/subscription`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load subscription'));

export const registerPeer = (data) =>
  fetch(`${BASE}/api/interviews/meeting-peer`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to register peer'));

// Returns null when the room has no host yet (404 from backend).
export const getPeer = async (roomId) => {
  const res = await fetch(`${BASE}/api/interviews/meeting-peer/${roomId}`, {
    headers: getAuthHeaders(false),
  });
  if (res.status === 404) return null;
  return jsonOrThrow(res, 'Failed to fetch peer');
};

// Graceful disconnect — remove our own peer entry from the room.
export const deletePeer = (roomId) =>
  fetch(`${BASE}/api/interviews/meeting-peer/${roomId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to remove peer'));

// Host-side teardown: drop the whole room registration.
export const deleteRoom = (roomId) =>
  fetch(`${BASE}/api/interviews/meeting-room/${roomId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to remove room'));

// Returns { hasAccess, currentUserName, otherParticipantName,
//           otherParticipantEmail, isInterviewer }
export const getMeetingAccess = (roomId) =>
  fetch(`${BASE}/api/interviews/meeting-access/${roomId}`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load meeting access'));

// Candidate's received feedbacks (full details, with skill ratings).
export const getMyFeedback = () =>
  fetch(`${BASE}/api/interviews/my-feedback`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load feedback'));

// ─── INTERVIEWER ──────────────────────────────────────────────
export const getInterviewerRequests = () =>
  fetch(`${BASE}/api/interviewer/requests`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load interviewer requests'));

export const acceptInterviewRequest = (id) =>
  fetch(`${BASE}/api/interviewer/requests/${id}/accept`, {
    method: 'POST',
    headers: getAuthHeaders(),
  }).then((r) => jsonOrThrow(r, 'Failed to accept request'));

export const declineInterviewRequest = (id) =>
  fetch(`${BASE}/api/interviewer/requests/${id}/decline`, {
    method: 'POST',
    headers: getAuthHeaders(),
  }).then((r) => jsonOrThrow(r, 'Failed to decline request'));

export const rescheduleInterview = (data) =>
  fetch(`${BASE}/api/interviewer/reschedule`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to reschedule'));

export const submitFeedback = (data) =>
  fetch(`${BASE}/api/interviewer/feedback`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to submit feedback'));

// Returns null when no feedback has been submitted yet (404 from backend).
export const getFeedbackByRequest = async (requestId) => {
  const res = await fetch(`${BASE}/api/interviewer/feedback/${requestId}`, {
    headers: getAuthHeaders(false),
  });
  if (res.status === 404) return null;
  return jsonOrThrow(res, 'Failed to load feedback');
};

// ─── INTERVIEWER SLOTS (self-service) ─────────────────────────
export const getMySlots = () =>
  fetch(`${BASE}/api/interviewer/slots`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load slots'));

export const addMySlot = (data) =>
  fetch(`${BASE}/api/interviewer/slots`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to add slot'));

export const deleteMySlot = (slotId) =>
  fetch(`${BASE}/api/interviewer/slots/${slotId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to delete slot'));

// Admin variant for deleting any interviewer's slot.
export const adminDeleteSlot = (slotId) =>
  fetch(`${BASE}/api/admin/interviewers/slots/${slotId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to delete slot'));

// ─── AI INTERVIEW ─────────────────────────────────────────────
export const checkAISessionAllowed = () =>
  fetch(`${BASE}/api/ai-interview/check`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to check AI session'));

export const startAISession = (data) =>
  fetch(`${BASE}/api/ai-interview/start`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to start AI session'));

export const endAISession = (data) =>
  fetch(`${BASE}/api/ai-interview/end`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to end AI session'));

export const getAISessionHistory = () =>
  fetch(`${BASE}/api/ai-interview/history`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load AI history'));

// Server-side Gemini turn: backend appends `userMessage` to `history` before
// calling Gemini, so the frontend should send history WITHOUT the current turn.
export const sendAIMessage = (data) =>
  fetch(`${BASE}/api/ai-interview/message`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to reach interviewer'));

export const evaluateAISession = (data) =>
  fetch(`${BASE}/api/ai-interview/evaluate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to evaluate session'));

// ─── PAYMENT / PLANS (candidate-facing) ───────────────────────
export const getPlans = () =>
  fetch(`${BASE}/api/payment/plans`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load plans'));

// Always returns a valid response — Free users get
// { planKey: "Free", billingCycle: "Free", endDate: null }.
export const getMyPaymentSubscription = () =>
  fetch(`${BASE}/api/payment/subscription`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load subscription'));

export const createPaymentOrder = (data) =>
  fetch(`${BASE}/api/payment/create-order`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to create order'));

export const verifyPayment = (data) =>
  fetch(`${BASE}/api/payment/verify`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Payment verification failed'));

export const getPaymentHistory = () =>
  fetch(`${BASE}/api/payment/history`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load payment history'));

// ─── ADMIN PLANS ──────────────────────────────────────────────
export const adminGetPlans = () =>
  fetch(`${BASE}/api/admin/subscription/plans`, {
    headers: getAuthHeaders(false),
  }).then((r) => jsonOrThrow(r, 'Failed to load plans'));

export const adminUpsertPlan = (data) =>
  fetch(`${BASE}/api/admin/subscription/plans`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  }).then((r) => jsonOrThrow(r, 'Failed to save plan'));

export const adminTogglePlan = (id) =>
  fetch(`${BASE}/api/admin/subscription/plans/${id}/toggle`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
  }).then((r) => jsonOrThrow(r, 'Failed to toggle plan'));

export const adminSeedPlans = () =>
  fetch(`${BASE}/api/admin/subscription/plans/seed`, {
    method: 'POST',
    headers: getAuthHeaders(),
  }).then((r) => jsonOrThrow(r, 'Failed to seed default plans'));

// Helper: pull a roomId out of `https://…/room/{roomId}`-style URLs.
export const extractRoomId = (meetingLink) => meetingLink?.split('/room/')[1] ?? '';
